import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useAuthStore } from '@stores/authStore';
import MainLayout from '@components/layouts/MainLayout';
import AuthLayout from '@components/layouts/AuthLayout';
import PublicLayout from '@components/layouts/PublicLayout';
import LoadingSpinner from '@components/ui/LoadingSpinner';

// Lazy load pages for code splitting
const WelcomePage = lazy(() => import('@pages/auth/WelcomePage'));
const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const SignupPage = lazy(() => import('@pages/auth/SignupPage'));
const SignupDetailsPage = lazy(() => import('@pages/auth/SignupDetailsPage'));
const SignupCompanyPage = lazy(() => import('@pages/auth/SignupCompanyPage'));
const InviteSignupPage = lazy(() => import('@pages/auth/InviteSignupPage'));
const ForgotPasswordPage = lazy(() => import('@pages/auth/ForgotPasswordPage'));
const VerifyOtpPage = lazy(() => import('@pages/auth/VerifyOtpPage'));
const VerifyEmailPage = lazy(() => import('@pages/auth/VerifyEmailPage'));
const ActivateAccountPage = lazy(() => import('@pages/auth/ActivateAccountPage'));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage'));
const FirstTimePasswordResetPage = lazy(() => import('@pages/auth/FirstTimePasswordResetPage'));
const OAuthCallbackPage = lazy(() => import('@pages/auth/OAuthCallbackPage'));
const SsoCallbackPage = lazy(() => import('@pages/auth/SsoCallbackPage'));
const DashboardPage = lazy(() => import('@pages/dashboard/DashboardPage'));
const UsersPage = lazy(() => import('@pages/users/UsersPage'));
const AppAnalyticsPage = lazy(() => import('@pages/apps/AppAnalyticsPage'));
const UsageStatisticsPage = lazy(() => import('@pages/apps/UsageStatisticsPage'));
const EngagementMetricsPage = lazy(() => import('@pages/apps/EngagementMetricsPage'));
const ManageAppListPage = lazy(() => import('@pages/apps/ManageAppListPage'));
const ManageAppGeneralPage = lazy(() => import('@pages/apps/ManageAppGeneralPage'));
const ManageAppPermissionPage = lazy(() => import('@pages/apps/ManageAppPermissionPage'));
const ManageAppRolesPage = lazy(() => import('@pages/apps/ManageAppRolesPage'));
const ReportsPage = lazy(() => import('@pages/reports/ReportsPage'));
const SubscriptionsPage = lazy(() => import('@pages/subscriptions/SubscriptionsPage'));
const PublicBrowseAppsPage = lazy(() => import('@pages/subscriptions/PublicBrowseAppsPage'));
const SelectSubscriptionPage = lazy(() => import('@pages/subscriptions/SelectSubscriptionPage'));
const ManageSubscriptionPage = lazy(() => import('@pages/subscriptions/ManageSubscriptionPage'));
const PaymentMethodPage = lazy(() => import('@pages/subscriptions/PaymentMethodPage'));
const AddCardPage = lazy(() => import('@pages/subscriptions/AddCardPage'));
const EditCardPage = lazy(() => import('@pages/subscriptions/EditCardPage'));
const ConfirmPaymentPage = lazy(() => import('@pages/subscriptions/ConfirmPaymentPage'));
const SettingsPage = lazy(() => import('@pages/settings/SettingsPage'));
const RolesPermissionsPage = lazy(() => import('@pages/roles-permissions/RolesPermissionsPage'));
const NotFoundPage = lazy(() => import('@pages/NotFoundPage'));

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user needs to reset password on first login
  if (user?.firstTimeLogin && window.location.pathname !== '/first-time-password-reset') {
    return <Navigate to="/first-time-password-reset" replace />;
  }

  return <>{children}</>;
}

// Public Route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const [searchParams] = useSearchParams();
  const hasToken = searchParams.get("token");
  const isVerified = searchParams.get("verified");

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  
  // Don't redirect if we have an activation token
  if (isAuthenticated && !(hasToken && isVerified === "true")) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { isLoading, setLoading } = useAuthStore();

  // Safety mechanism: Ensure loading state is cleared after mount
  useEffect(() => {
    // Set a timeout to force loading to false if it's still true after 2 seconds
    // This prevents infinite loading states
    if (isLoading) {
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [isLoading, setLoading]);

  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        {/* Public Routes */}
        <Route
          element={
            <PublicRoute>
              <AuthLayout />
            </PublicRoute>
          }
        >
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/signup/details" element={<SignupDetailsPage />} />
          <Route path="/signup/company" element={<SignupCompanyPage />} />
          <Route path="/signup/invite" element={<InviteSignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/verify-otp" element={<VerifyOtpPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/activate-account" element={<ActivateAccountPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/oauth2/callback" element={<OAuthCallbackPage />} />
          <Route path="/sso/callback" element={<SsoCallbackPage />} />
        </Route>

        {/* Public App Browsing Route */}
        <Route
          path="/tools"
          element={<PublicLayout><PublicBrowseAppsPage /></PublicLayout>}
        />

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/apps/analytics" element={<AppAnalyticsPage />} />
          <Route path="/apps/analytics/:toolId/usage" element={<UsageStatisticsPage />} />
          <Route path="/apps/analytics/:toolId/engagement" element={<EngagementMetricsPage />} />
          <Route path="/apps/manage" element={<ManageAppListPage />} />
          <Route path="/manage-app/:toolId" element={<ManageAppGeneralPage />} />
          <Route path="/manage-app/:toolId/permissions" element={<ManageAppPermissionPage />} />
          <Route path="/manage-app/:toolId/roles" element={<ManageAppRolesPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/roles-permissions" element={<RolesPermissionsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        {/* First-time password reset (standalone, no layout) */}
        <Route
          path="/first-time-password-reset"
          element={
            <ProtectedRoute>
              <FirstTimePasswordResetPage />
            </ProtectedRoute>
          }
        />

        {/* Full-page Protected Routes (custom layouts) */}
        <Route
          path="/subscriptions/select/:toolId"
          element={
            <ProtectedRoute>
              <SelectSubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions/manage/:toolId"
          element={
            <ProtectedRoute>
              <ManageSubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions/payment"
          element={
            <ProtectedRoute>
              <PaymentMethodPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions/payment-card-add"
          element={
            <ProtectedRoute>
              <AddCardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions/payment-card-edit/:cardId"
          element={
            <ProtectedRoute>
              <EditCardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions/confirm-payment"
          element={
            <ProtectedRoute>
              <ConfirmPaymentPage />
            </ProtectedRoute>
          }
        />

        {/* Redirects and 404 */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
